
import * as d3 from "d3";
import Libra from "libra-vis";
import LibraManager from "../../core/LibraManager";
import { compileDSL } from "../../scripts/dsl-compiler";

export default async function init() {
    const container = document.getElementById("LibraPlayground");
    if (!container) return;
    container.innerHTML = "";

    const MARGIN = { top: 30, right: 10, bottom: 10, left: 30 };
    const WIDTH = 480 - MARGIN.left - MARGIN.right;
    const HEIGHT = 300 - MARGIN.top - MARGIN.bottom;
    const AXIS_AREA_RATIO = 0.85;
    const AXIS_AREA_WIDTH = WIDTH * AXIS_AREA_RATIO;
    const AXIS_AREA_OFFSET_X = (WIDTH - AXIS_AREA_WIDTH) / 2;

    const { parallelData, dimensions } = loadData();

    const svg = d3.select(container).append("svg")
        .attr("width", WIDTH + MARGIN.left + MARGIN.right)
        .attr("height", HEIGHT + MARGIN.top + MARGIN.bottom);

    // Setup Scales
    const x = d3.scaleBand()
        .range([0, AXIS_AREA_WIDTH])
        .domain(dimensions)
        .padding(0.01);

    const y = {};
    dimensions.forEach(dimension => {
        y[dimension] = d3.scaleLinear()
            .domain(d3.extent(parallelData, d => d[dimension]))
            .range([HEIGHT, 0]);
    });

    const { linesLayer, axisLayers, headersLayer } = renderMainVisualization(svg, parallelData, dimensions, x, y, MARGIN, AXIS_AREA_WIDTH, AXIS_AREA_OFFSET_X, HEIGHT);
    await mountInteraction(linesLayer, axisLayers, headersLayer, parallelData, dimensions, x, y, MARGIN, AXIS_AREA_OFFSET_X);
}

function loadData() {
    const miserables = {
        nodes: [
            { nodeName: "Myriel", group: 1 },
            { nodeName: "Napoleon", group: 1 },
            { nodeName: "Mlle. Baptistine", group: 1 },
            { nodeName: "Mme. Magloire", group: 1 },
            { nodeName: "Countess de Lo", group: 1 },
            { nodeName: "Geborand", group: 1 },
            { nodeName: "Champtercier", group: 1 },
            { nodeName: "Cravatte", group: 1 },
            { nodeName: "Count", group: 1 },
            { nodeName: "Old Man", group: 1 },
            { nodeName: "Labarre", group: 2 },
            { nodeName: "Valjean", group: 2 },
            { nodeName: "Marguerite", group: 3 },
            { nodeName: "Mme. de R", group: 2 },
            { nodeName: "Isabeau", group: 2 },
            { nodeName: "Gervais", group: 2 },
            { nodeName: "Tholomyes", group: 3 },
            { nodeName: "Listolier", group: 3 },
            { nodeName: "Fameuil", group: 3 },
            { nodeName: "Blacheville", group: 3 },
            { nodeName: "Favourite", group: 3 },
            { nodeName: "Dahlia", group: 3 },
            { nodeName: "Zephine", group: 3 },
            { nodeName: "Fantine", group: 3 },
            { nodeName: "Mme. Thenardier", group: 4 },
            { nodeName: "Thenardier", group: 4 },
            { nodeName: "Cosette", group: 5 },
            { nodeName: "Javert", group: 4 },
            { nodeName: "Fauchelevent", group: 0 },
            { nodeName: "Bamatabois", group: 2 },
            { nodeName: "Perpetue", group: 3 },
            { nodeName: "Simplice", group: 2 },
            { nodeName: "Scaufflaire", group: 2 },
            { nodeName: "Woman 1", group: 2 },
            { nodeName: "Judge", group: 2 },
            { nodeName: "Champmathieu", group: 2 },
            { nodeName: "Brevet", group: 2 },
            { nodeName: "Chenildieu", group: 2 },
            { nodeName: "Cochepaille", group: 2 },
            { nodeName: "Pontmercy", group: 4 },
            { nodeName: "Boulatruelle", group: 6 },
            { nodeName: "Eponine", group: 4 },
            { nodeName: "Anzelma", group: 4 },
            { nodeName: "Woman 2", group: 5 },
            { nodeName: "Mother Innocent", group: 0 },
            { nodeName: "Gribier", group: 0 },
            { nodeName: "Jondrette", group: 7 },
            { nodeName: "Mme. Burgon", group: 7 },
            { nodeName: "Gavroche", group: 8 },
            { nodeName: "Gillenormand", group: 5 },
            { nodeName: "Magnon", group: 5 },
            { nodeName: "Mlle. Gillenormand", group: 5 },
            { nodeName: "Mme. Pontmercy", group: 5 },
            { nodeName: "Mlle. Vaubois", group: 5 },
            { nodeName: "Lt. Gillenormand", group: 5 },
            { nodeName: "Marius", group: 8 },
            { nodeName: "Baroness T", group: 5 },
            { nodeName: "Mabeuf", group: 8 },
            { nodeName: "Enjolras", group: 8 },
            { nodeName: "Combeferre", group: 8 },
            { nodeName: "Prouvaire", group: 8 },
            { nodeName: "Feuilly", group: 8 },
            { nodeName: "Courfeyrac", group: 8 },
            { nodeName: "Bahorel", group: 8 },
            { nodeName: "Bossuet", group: 8 },
            { nodeName: "Joly", group: 8 },
            { nodeName: "Grantaire", group: 8 },
            { nodeName: "Mother Plutarch", group: 9 },
            { nodeName: "Gueulemer", group: 4 },
            { nodeName: "Babet", group: 4 },
            { nodeName: "Claquesous", group: 4 },
            { nodeName: "Montparnasse", group: 4 },
            { nodeName: "Toussaint", group: 5 },
            { nodeName: "Child 1", group: 10 },
            { nodeName: "Child 2", group: 10 },
            { nodeName: "Brujon", group: 4 },
            { nodeName: "Mme. Hucheloup", group: 8 },
        ],
        links: [
            { source: 1, target: 0, value: 1 },
            { source: 2, target: 0, value: 8 },
            { source: 3, target: 0, value: 10 },
            { source: 3, target: 2, value: 6 },
            { source: 4, target: 0, value: 1 },
            { source: 5, target: 0, value: 1 },
            { source: 6, target: 0, value: 1 },
            { source: 7, target: 0, value: 1 },
            { source: 8, target: 0, value: 2 },
            { source: 9, target: 0, value: 1 },
            { source: 11, target: 10, value: 1 },
            { source: 11, target: 3, value: 3 },
            { source: 11, target: 2, value: 3 },
            { source: 11, target: 0, value: 5 },
            { source: 12, target: 11, value: 1 },
            { source: 13, target: 11, value: 1 },
            { source: 14, target: 11, value: 1 },
            { source: 15, target: 11, value: 1 },
            { source: 17, target: 16, value: 4 },
            { source: 18, target: 16, value: 4 },
            { source: 18, target: 17, value: 4 },
            { source: 19, target: 16, value: 4 },
            { source: 19, target: 17, value: 4 },
            { source: 19, target: 18, value: 4 },
            { source: 20, target: 16, value: 3 },
            { source: 20, target: 17, value: 3 },
            { source: 20, target: 18, value: 3 },
            { source: 20, target: 19, value: 4 },
            { source: 21, target: 16, value: 3 },
            { source: 21, target: 17, value: 3 },
            { source: 21, target: 18, value: 3 },
            { source: 21, target: 19, value: 3 },
            { source: 21, target: 20, value: 5 },
            { source: 22, target: 16, value: 3 },
            { source: 22, target: 17, value: 3 },
            { source: 22, target: 18, value: 3 },
            { source: 22, target: 19, value: 3 },
            { source: 22, target: 20, value: 4 },
            { source: 22, target: 21, value: 4 },
            { source: 23, target: 16, value: 3 },
            { source: 23, target: 17, value: 3 },
            { source: 23, target: 18, value: 3 },
            { source: 23, target: 19, value: 3 },
            { source: 23, target: 20, value: 4 },
            { source: 23, target: 21, value: 4 },
            { source: 23, target: 22, value: 4 },
            { source: 23, target: 12, value: 2 },
            { source: 23, target: 11, value: 9 },
            { source: 24, target: 23, value: 2 },
            { source: 24, target: 11, value: 7 },
            { source: 25, target: 24, value: 13 },
            { source: 25, target: 23, value: 1 },
            { source: 25, target: 11, value: 12 },
            { source: 26, target: 24, value: 4 },
            { source: 26, target: 11, value: 31 },
            { source: 26, target: 16, value: 1 },
            { source: 26, target: 25, value: 1 },
            { source: 27, target: 11, value: 17 },
            { source: 27, target: 23, value: 5 },
            { source: 27, target: 25, value: 5 },
            { source: 27, target: 24, value: 1 },
            { source: 27, target: 26, value: 1 },
            { source: 28, target: 11, value: 8 },
            { source: 28, target: 27, value: 1 },
            { source: 29, target: 23, value: 1 },
            { source: 29, target: 27, value: 1 },
            { source: 29, target: 11, value: 2 },
            { source: 30, target: 23, value: 1 },
            { source: 31, target: 30, value: 2 },
            { source: 31, target: 11, value: 3 },
            { source: 31, target: 23, value: 2 },
            { source: 31, target: 27, value: 1 },
            { source: 32, target: 11, value: 1 },
            { source: 33, target: 11, value: 2 },
            { source: 33, target: 27, value: 1 },
            { source: 34, target: 11, value: 3 },
            { source: 34, target: 29, value: 2 },
            { source: 35, target: 11, value: 3 },
            { source: 35, target: 34, value: 3 },
            { source: 35, target: 29, value: 2 },
            { source: 36, target: 34, value: 2 },
            { source: 36, target: 35, value: 2 },
            { source: 36, target: 11, value: 2 },
            { source: 36, target: 29, value: 1 },
            { source: 37, target: 34, value: 2 },
            { source: 37, target: 35, value: 2 },
            { source: 37, target: 36, value: 2 },
            { source: 37, target: 11, value: 2 },
            { source: 37, target: 29, value: 1 },
            { source: 38, target: 34, value: 2 },
            { source: 38, target: 35, value: 2 },
            { source: 38, target: 36, value: 2 },
            { source: 38, target: 37, value: 2 },
            { source: 38, target: 11, value: 2 },
            { source: 38, target: 29, value: 1 },
            { source: 39, target: 25, value: 1 },
            { source: 40, target: 25, value: 1 },
            { source: 41, target: 24, value: 2 },
            { source: 41, target: 25, value: 3 },
            { source: 42, target: 41, value: 2 },
            { source: 42, target: 25, value: 2 },
            { source: 42, target: 24, value: 1 },
            { source: 43, target: 11, value: 3 },
            { source: 43, target: 26, value: 1 },
            { source: 43, target: 27, value: 1 },
            { source: 44, target: 28, value: 3 },
            { source: 44, target: 11, value: 1 },
            { source: 45, target: 28, value: 2 },
            { source: 47, target: 46, value: 1 },
            { source: 48, target: 47, value: 2 },
            { source: 48, target: 25, value: 1 },
            { source: 48, target: 27, value: 1 },
            { source: 48, target: 11, value: 1 },
            { source: 49, target: 26, value: 3 },
            { source: 49, target: 11, value: 2 },
            { source: 50, target: 49, value: 1 },
            { source: 50, target: 24, value: 1 },
            { source: 51, target: 49, value: 9 },
            { source: 51, target: 26, value: 2 },
            { source: 51, target: 11, value: 2 },
            { source: 52, target: 51, value: 1 },
            { source: 52, target: 39, value: 1 },
            { source: 53, target: 51, value: 1 },
            { source: 54, target: 51, value: 2 },
            { source: 54, target: 49, value: 1 },
            { source: 54, target: 26, value: 1 },
            { source: 55, target: 51, value: 6 },
            { source: 55, target: 49, value: 12 },
            { source: 55, target: 39, value: 1 },
            { source: 55, target: 54, value: 1 },
            { source: 55, target: 26, value: 21 },
            { source: 55, target: 11, value: 19 },
            { source: 55, target: 16, value: 1 },
            { source: 55, target: 25, value: 2 },
            { source: 55, target: 41, value: 5 },
            { source: 55, target: 48, value: 4 },
            { source: 56, target: 49, value: 1 },
            { source: 56, target: 55, value: 1 },
            { source: 57, target: 55, value: 1 },
            { source: 57, target: 41, value: 1 },
            { source: 57, target: 48, value: 1 },
            { source: 58, target: 55, value: 7 },
            { source: 58, target: 48, value: 7 },
            { source: 58, target: 27, value: 6 },
            { source: 58, target: 57, value: 1 },
            { source: 58, target: 11, value: 4 },
            { source: 59, target: 58, value: 15 },
            { source: 59, target: 55, value: 5 },
            { source: 59, target: 48, value: 6 },
            { source: 59, target: 57, value: 2 },
            { source: 60, target: 48, value: 1 },
            { source: 60, target: 58, value: 4 },
            { source: 60, target: 59, value: 2 },
            { source: 61, target: 48, value: 2 },
            { source: 61, target: 58, value: 6 },
            { source: 61, target: 60, value: 2 },
            { source: 61, target: 59, value: 5 },
            { source: 61, target: 57, value: 1 },
            { source: 61, target: 55, value: 1 },
            { source: 62, target: 55, value: 9 },
            { source: 62, target: 58, value: 17 },
            { source: 62, target: 59, value: 13 },
            { source: 62, target: 48, value: 7 },
            { source: 62, target: 57, value: 2 },
            { source: 62, target: 41, value: 1 },
            { source: 62, target: 61, value: 6 },
            { source: 62, target: 60, value: 3 },
            { source: 63, target: 59, value: 5 },
            { source: 63, target: 48, value: 5 },
            { source: 63, target: 62, value: 6 },
            { source: 63, target: 57, value: 2 },
            { source: 63, target: 58, value: 4 },
            { source: 63, target: 61, value: 3 },
            { source: 63, target: 60, value: 2 },
            { source: 63, target: 55, value: 1 },
            { source: 64, target: 55, value: 5 },
            { source: 64, target: 62, value: 12 },
            { source: 64, target: 48, value: 5 },
            { source: 64, target: 63, value: 4 },
            { source: 64, target: 58, value: 10 },
            { source: 64, target: 61, value: 6 },
            { source: 64, target: 60, value: 2 },
            { source: 64, target: 59, value: 9 },
            { source: 64, target: 57, value: 1 },
            { source: 64, target: 11, value: 1 },
            { source: 65, target: 63, value: 5 },
            { source: 65, target: 64, value: 7 },
            { source: 65, target: 48, value: 3 },
            { source: 65, target: 62, value: 5 },
            { source: 65, target: 58, value: 5 },
            { source: 65, target: 61, value: 5 },
            { source: 65, target: 60, value: 2 },
            { source: 65, target: 59, value: 5 },
            { source: 65, target: 57, value: 1 },
            { source: 65, target: 55, value: 2 },
            { source: 66, target: 64, value: 3 },
            { source: 66, target: 58, value: 3 },
            { source: 66, target: 59, value: 1 },
            { source: 66, target: 62, value: 2 },
            { source: 66, target: 65, value: 2 },
            { source: 66, target: 48, value: 1 },
            { source: 66, target: 63, value: 1 },
            { source: 66, target: 61, value: 1 },
            { source: 66, target: 60, value: 1 },
            { source: 67, target: 57, value: 3 },
            { source: 68, target: 25, value: 5 },
            { source: 68, target: 11, value: 1 },
            { source: 68, target: 24, value: 1 },
            { source: 68, target: 27, value: 1 },
            { source: 68, target: 48, value: 1 },
            { source: 68, target: 41, value: 1 },
            { source: 69, target: 25, value: 6 },
            { source: 69, target: 68, value: 6 },
            { source: 69, target: 11, value: 1 },
            { source: 69, target: 24, value: 1 },
            { source: 69, target: 27, value: 2 },
            { source: 69, target: 48, value: 1 },
            { source: 69, target: 41, value: 1 },
            { source: 70, target: 25, value: 4 },
            { source: 70, target: 69, value: 4 },
            { source: 70, target: 68, value: 4 },
            { source: 70, target: 11, value: 1 },
            { source: 70, target: 24, value: 1 },
            { source: 70, target: 27, value: 1 },
            { source: 70, target: 41, value: 1 },
            { source: 70, target: 58, value: 1 },
            { source: 71, target: 27, value: 1 },
            { source: 71, target: 69, value: 2 },
            { source: 71, target: 68, value: 2 },
            { source: 71, target: 70, value: 2 },
            { source: 71, target: 11, value: 1 },
            { source: 71, target: 48, value: 1 },
            { source: 71, target: 41, value: 1 },
            { source: 71, target: 25, value: 1 },
            { source: 72, target: 26, value: 2 },
            { source: 72, target: 27, value: 1 },
            { source: 72, target: 11, value: 1 },
            { source: 73, target: 48, value: 2 },
            { source: 74, target: 48, value: 2 },
            { source: 74, target: 73, value: 3 },
            { source: 75, target: 69, value: 3 },
            { source: 75, target: 68, value: 3 },
            { source: 75, target: 25, value: 3 },
            { source: 75, target: 48, value: 1 },
            { source: 75, target: 41, value: 1 },
            { source: 75, target: 70, value: 1 },
            { source: 75, target: 71, value: 1 },
            { source: 76, target: 64, value: 1 },
            { source: 76, target: 65, value: 1 },
            { source: 76, target: 66, value: 1 },
            { source: 76, target: 63, value: 1 },
            { source: 76, target: 62, value: 1 },
            { source: 76, target: 48, value: 1 },
            { source: 76, target: 58, value: 1 },
        ],
    };

    // Simplified dimensions
    const dimensions = ["Valjean", "Fantine", "Marius", "Javert"];

    const parallelData = dimensions.map(d => {
        const obj = { name: d };
        dimensions.forEach(dim => {
            if (d === dim) {
                obj[dim] = 1;
            } else {
                const link = miserables.links.find(l =>
                    (miserables.nodes[l.source].nodeName === d && miserables.nodes[l.target].nodeName === dim) ||
                    (miserables.nodes[l.source].nodeName === dim && miserables.nodes[l.target].nodeName === d)
                );
                obj[dim] = link ? link.value / 3 : 0;
            }
        });
        return obj;
    });

    return { parallelData, dimensions };
}

function renderMainVisualization(svg, parallelData, dimensions, x, y, MARGIN, AXIS_AREA_WIDTH, AXIS_AREA_OFFSET_X, HEIGHT) {
    // 1. Create Lines Layer (Background)
    const linesLayer = LibraManager.getOrCreateLayer(svg, "linesLayer", AXIS_AREA_WIDTH, HEIGHT, MARGIN.left + AXIS_AREA_OFFSET_X, MARGIN.top);

    // 2. Create Axis Layers (Middle)
    const axisLayers = {};
    dimensions.forEach(dim => {
        const layerName = dim.replace(/\./g, '_');
        let axisLayer = null;
        axisLayer = LibraManager.getOrCreateLayer(svg, "axisLayer-" + layerName, x.bandwidth(), HEIGHT, x(dim) + MARGIN.left + AXIS_AREA_OFFSET_X, MARGIN.top);
        
        axisLayers[dim] = axisLayer;

        const axisG = d3.select(axisLayer.getGraphic());
        axisG.attr("class", "axis");

        // Clear previous content and create a centered group
        axisG.selectAll("*").remove();
        const centeredG = axisG.append("g")
            .attr("transform", `translate(${x.bandwidth() / 2}, 0)`);

        centeredG.call(d3.axisLeft(y[dim]));
    });

    // 3. Create Headers Layer (Foreground)
    const headersLayer = LibraManager.getOrCreateLayer(svg, "headersLayer", AXIS_AREA_WIDTH, MARGIN.top, MARGIN.left + AXIS_AREA_OFFSET_X, 0);

    const linesG = d3.select(linesLayer.getGraphic());
    linesG.attr("class", "lines");

    const headersG = d3.select(headersLayer.getGraphic());
    headersG.attr("class", "headers");

    // Draw lines on linesLayer
    const line = d3.line()
        .defined((d) => !isNaN(d[1]))
        .x((d, i) => x(dimensions[i]) + x.bandwidth() / 2)
        .y((d) => d[1]);

    linesG.selectAll(".line")
        .data(parallelData)
        .join("path")
        .attr("class", "line")
        .attr("d", (d) => line(dimensions.map((p) => [p, y[p](d[p])])))
        .attr("fill", "none")
        .attr("stroke", "steelblue")
        .attr("opacity", 1);

    // Draw headers on headersLayer
    headersG.selectAll(".header")
        .data(dimensions)
        .join("text")
        .attr("class", "header")
        .attr("transform", (d) => `translate(${x(d) + x.bandwidth() / 2},${MARGIN.top})`)
        .attr("text-anchor", "middle")
        .attr("y", -9)
        .text((d) => d)
        .attr("fill", "black")
        .attr("cursor", "grab"); // Add cursor to indicate interaction

    return { linesLayer, axisLayers, headersLayer };
}

async function mountInteraction(linesLayer, axisLayers, headersLayer, parallelData, dimensions, x, y, MARGIN, AXIS_AREA_OFFSET_X) {
    const redrawParallel = (newNames, newX) => {
        // Update Lines Layer
        const linesG = d3.select(linesLayer.getGraphic());
        linesG.selectAll(".line").remove();

        const line = d3.line()
            .defined((d) => !isNaN(d[1]))
            .x((d, i) => newX(newNames[i]) + newX.bandwidth() / 2)
            .y((d) => d[1]);

        linesG.selectAll(".line")
            .data(parallelData)
            .join("path")
            .attr("class", "line")
            .attr("d", (d) => line(newNames.map((p) => [p, y[p](d[p])])))
            .attr("fill", "none")
            .attr("stroke", "steelblue")
            .attr("opacity", 1);

        // Update Axis Layers positions
        newNames.forEach(dim => {
            const layer = axisLayers[dim];
            if (layer) {

                layer.setOffsetCascade(newX(dim) + MARGIN.left + AXIS_AREA_OFFSET_X, MARGIN.top);
                const axisG = d3.select(layer.getGraphic());
                const centeredG = axisG.select("g");
                if (!centeredG.empty()) {
                    centeredG.call(d3.axisLeft(y[dim]));
                }
            }
        });

        // Update Headers Layer
        const headersG = d3.select(headersLayer.getGraphic());
        headersG.selectAll("*").remove();

        headersG.selectAll(".header")
            .data(newNames)
            .join("text")
            .attr("class", "header")
            .attr("transform", (d) => `translate(${newX(d) + newX.bandwidth() / 2},${MARGIN.top})`)
            .attr("text-anchor", "middle")
            .attr("y", -9)
            .text((d) => d)
            .attr("fill", "black")
            .attr("cursor", "grab");

        // Trigger selection layer update
        LibraManager.updateLinkSelection(linesLayer);
    };

    const axisInteractions = dimensions.flatMap((dim) => {
        const layerName = dim.replace(/\./g, "_");
        return [
            {
                instrument: "axisSelection",
                trigger: { type: "brushy" },
                target: { layer: `axisLayer-${layerName}` },
                feedback: {
                    redrawFunc: {
                        highlightAttrValues: {
                            stroke: "#ff0000",
                            "stroke-width": 2
                        },
                        baseOpacity: 1
                    },
                    service: { selectionMode: "intersection" },
                    context: {
                        link: { layers: [linesLayer] },
                        scale: y[dim],
                        attrName: dim
                    }
                }
            }
        ];
    });

    const interactions = [
        {
            instrument: "reorder",
            trigger: { type: "drag" },
            target: { layer: "headersLayer" },
            feedback: {
                redrawFunc: redrawParallel,
                service: { reorderDirection: "x" },
                feedforward: {
                    sourceLayer: Object.values(axisLayers),
                    // offset: { x: MARGIN.left + AXIS_AREA_OFFSET_X, y: 0 }
                },
                context: {
                    names: dimensions,
                    scales: { x }
                }
            }
        },
        ...axisInteractions
    ];

    const layersByName = { headersLayer, linesLayer };
    dimensions.forEach(dim => {
        const layerName = dim.replace(/\./g, '_');
        layersByName[`axisLayer-${layerName}`] = axisLayers[dim];
    });

    await compileDSL(interactions, {
        layersByName
    }, { execute: true });

    await Libra.createHistoryTrack();
}
//这个例子拖拽的轴和被重排的轴不一样，疑似有位置偏移，但是普通paraCoord没有